import { Entity } from "@common/types/entity";
import { FinancialAccount } from "@common/types/financialAccount";
import { InvoiceSource } from "@common/types/invoice";
import { findToggledValue } from "@components/pageHeader/filters/FilterPickerList/findToggledValue";
import type { AccountFilterRow, EntityAccounts } from "./accountFilterRows";

export interface AccountFilters {
    accountIds?: number[];
    excludedAccountIds?: number[];
    subaccountIds?: string[];
    hasNoFinancialAccount?: boolean;
    invoiceSources?: InvoiceSource[];
}

export const accountMatches = (
    account: Pick<FinancialAccount, "name" | "accountNumberMask">,
    search: string,
) =>
    (account.name ?? "").toLowerCase().includes(search.toLowerCase()) ||
    (account.accountNumberMask ?? "")
        .toLowerCase()
        .includes(search.toLowerCase());

const toggleId = <T>(ids: readonly T[], id: T): T[] =>
    ids.includes(id)
        ? ids.filter((selectedId) => selectedId !== id)
        : [...ids, id];

/**
 * Each entity with its accounts that match `search`, skipping entities left
 * with none. Only these accounts are rows, so an entity ticked mid-search
 * selects the accounts on screen.
 */
export const buildDisplayedEntityAccounts = ({
    entities,
    accounts,
    search,
    isBusinessOnly,
}: {
    entities: Entity[];
    accounts: FinancialAccount[];
    search: string;
    isBusinessOnly: boolean;
}): EntityAccounts[] =>
    entities
        .filter((entity) => !isBusinessOnly || entity.isBusiness)
        .map((entity) => ({
            entity,
            accounts: accounts.filter(
                (account) =>
                    account.entity?.id === entity.id &&
                    accountMatches(account, search),
            ),
        }))
        .filter(({ accounts: entityAccounts }) => entityAccounts.length > 0);

/**
 * The row a picker change toggled. Values with no row (a saved account that
 * was deleted or is hidden by the search) are dropped on both sides, or one
 * could be taken for the row the user ticked.
 */
export const findToggledRow = ({
    value,
    next,
    rowByValue,
}: {
    value: readonly string[];
    next: readonly string[];
    rowByValue: ReadonlyMap<string, AccountFilterRow>;
}): AccountFilterRow | undefined => {
    const isKnownValue = (rowValue: string) => rowByValue.has(rowValue);
    const toggledValue = findToggledValue(
        value.filter(isKnownValue),
        next.filter(isKnownValue),
    );
    return toggledValue === undefined
        ? undefined
        : rowByValue.get(toggledValue);
};

/**
 * The filter change that ticking or unticking `row` makes, or `undefined`
 * when it changes nothing (an entity row in single select).
 */
export const applyRowToggle = ({
    row,
    filters,
    isSingleSelect,
}: {
    row: AccountFilterRow;
    filters: AccountFilters;
    isSingleSelect: boolean;
}): AccountFilters | undefined => {
    const accountIds = filters.accountIds ?? [];
    switch (row.kind) {
        case "kick":
            return {
                invoiceSources: filters.invoiceSources?.includes(
                    InvoiceSource.MANUAL,
                )
                    ? undefined
                    : [InvoiceSource.MANUAL],
            };
        case "noAccount":
            return {
                hasNoFinancialAccount: filters.hasNoFinancialAccount
                    ? undefined
                    : true,
            };
        case "entity": {
            if (isSingleSelect) {
                return undefined;
            }
            const isEverySelected = row.accountIds.every((id) =>
                accountIds.includes(id),
            );
            return {
                accountIds: isEverySelected
                    ? accountIds.filter((id) => !row.accountIds.includes(id))
                    : [...new Set([...accountIds, ...row.accountIds])],
            };
        }
        case "account":
            return {
                accountIds: isSingleSelect
                    ? [row.account.id]
                    : toggleId(accountIds, row.account.id),
            };
        case "subaccount":
            return {
                subaccountIds: toggleId(
                    filters.subaccountIds ?? [],
                    row.subaccountId,
                ),
            };
    }
};
